import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { ApiFailure } from '@/api/client'
import { ErrorState, Loading } from '@/ui/states'

/** How long to wait for the webhook before saying so. */
const ATTEMPTS = 10
const INTERVAL_MS = 2000

/**
 * Where the provider sends the browser back to after a hosted checkout.
 *
 * **It polls, and the reason is that the browser is not the source of truth.**
 * The customer authorising a mandate on Razorpay's page tells AugRank nothing;
 * the `subscription.activated` webhook does. That usually lands within a second
 * or two, and occasionally before the redirect — so this page asks the API what
 * the subscription looks like now, a few times, and says plainly what it found.
 *
 * **Nothing here is trusted from the URL.** A provider return carries query
 * parameters, and treating any of them as "payment succeeded" would mean a
 * customer could type the URL and activate their own plan. The only thing that
 * moves a subscription is a signed webhook.
 *
 * **Waiting is not failure.** If the webhook has not landed by the last
 * attempt, the page says the payment is being confirmed rather than claiming
 * anything went wrong — because at that point nothing has, and the honest state
 * is that AugRank does not know yet.
 */
export function CheckoutReturn(): JSX.Element {
  const navigate = useNavigate()
  const [attempt, setAttempt] = useState(0)
  const [status, setStatus] = useState<string | null>(null)
  // The real failure, kept rather than reconstructed: `ErrorState` shows the
  // request id, and an invented `ApiFailure` would show an empty one — which
  // is the only thing support can trace a report by.
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    // WEB-12. `cancelled` only silences the callback; the timer itself kept
    // running, and because the effect re-runs on every `attempt` the pending
    // ones stacked up. Held here rather than in `look()` so the cleanup below
    // can reach it.
    let timer: number | undefined

    async function look(): Promise<void> {
      const result = await call(() =>
        api.GET('/api/v1/billing/subscription', { signal: controller.signal }),
      )
      if (cancelled) return
      if (!result.ok) {
        setFailure(result.failure)
        return
      }
      const current = result.data.subscription
      setStatus(current?.status ?? null)
      if (current && current.status !== 'trialing') {
        void navigate('/billing', { replace: true })
        return
      }
      if (attempt < ATTEMPTS) {
        timer = window.setTimeout(() => {
          if (!cancelled) setAttempt((value) => value + 1)
        }, INTERVAL_MS)
      }
    }

    void look()
    return () => {
      cancelled = true
      controller.abort()
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [attempt, navigate])

  if (failure) {
    return (
      <ErrorState
        failure={failure}
        onRetry={() => {
          setFailure(null)
          setAttempt(0)
        }}
      />
    )
  }

  if (attempt < ATTEMPTS) return <Loading label="Confirming your payment" />

  return (
    <div className="ar-stack" style={{ maxWidth: '32rem', margin: '0 auto' }}>
      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Your payment is still being confirmed</strong> Your provider has not told us the
          outcome yet. This usually takes seconds; nothing is wrong, and nothing will be charged
          twice.
          {status === 'trialing' ? ' Your trial is running in the meantime.' : ''}
        </div>
      </div>
      <Link className="ar-btn ar-btn--primary" to="/billing">
        Go to billing
      </Link>
    </div>
  )
}
